import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Box, Paper, Tabs, Tab, Typography, IconButton, CircularProgress, Snackbar
} from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import axios from 'axios'
import InBidRateTable from './InBidRateTable'
import SaveAsGlobalModal from './SaveAsGlobalModal'
import RateChangeHistory from './RateChangeHistory'

const InBidRatesView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const { url } = props

  const [tab, setTab] = useState(0)
  const [project, setProject] = useState(null)
  const [clientName, setClientName] = useState('')
  const [laborRates, setLaborRates] = useState([])
  const [equipmentRates, setEquipmentRates] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalChange, setModalChange] = useState(null)
  const [toast, setToast] = useState(null)

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      const projectRes = await axios.get(`${url}/api/bid-estimation/projects/${projectId}`)
      setProject(projectRes.data)
      setClientName(projectRes.data.client || '')

      const clientId = projectRes.data.clientId
      const qs = `?projectId=${projectId}${clientId ? `&clientId=${clientId}` : ''}`
      const [ratesRes, historyRes] = await Promise.all([
        axios.get(`${url}/api/bid-estimation/rates/resolved${qs}`),
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/rate-change-history`)
      ])
      setLaborRates(ratesRes.data.laborRates || [])
      setEquipmentRates(ratesRes.data.equipmentRates || [])
      setEvents(historyRes.data || [])
    } catch (err) {
      console.error('InBidRatesView fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [url, projectId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // rateType is passed explicitly from the InBidRateTable component (via its `rateType` prop)
  const handleSave = async (row, editValues, rateType) => {
    try {
      const res = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/rate-override`,
        {
          rateType,
          roleKey: row.roleKey,
          hourlyRate: editValues.hourlyRate,
          defaultCrewSize: rateType === 'labor' ? editValues.defaultCrewSize : undefined
        }
      )
      setToast(`Updated ${row.role || row.name} — ${res.data.affectedComponentCount} components recalculated`)

      // Trigger "Save as global?" modal
      setModalChange({
        rateType,
        roleKey: row.roleKey,
        roleName: row.role || row.name,
        oldRate: res.data.event.oldRate,
        newRate: res.data.event.newRate,
        hourlyRate: editValues.hourlyRate,
        defaultCrewSize: editValues.defaultCrewSize
      })

      await fetchAll()
    } catch (err) {
      setToast(`Failed: ${err.response?.data?.error || err.message}`)
    }
  }

  const handleReset = async (row, rateType) => {
    const latest = events.find(e => !e.reverted && e.rateType === rateType && e.roleKey === row.roleKey)
    if (!latest) {
      setToast('No override event found to reset')
      return
    }
    try {
      await axios.post(`${url}/api/bid-estimation/rate-change-events/${latest._id}/revert`)
      setToast(`Reset ${row.role || row.name}`)
      await fetchAll()
    } catch (err) {
      setToast(`Failed: ${err.response?.data?.error || err.message}`)
    }
  }

  const handleRevert = async (event) => {
    try {
      await axios.post(`${url}/api/bid-estimation/rate-change-events/${event._id}/revert`)
      setToast('Change reverted')
      await fetchAll()
    } catch (err) {
      setToast(`Failed: ${err.response?.data?.error || err.message}`)
      throw err
    }
  }

  const handleApplyGlobal = async ({ updateEntity, updateClient }) => {
    if (!modalChange) return
    const { rateType, roleKey, hourlyRate, defaultCrewSize } = modalChange
    const endpoint = rateType === 'labor' ? 'labor-rates' : 'equipment-rates'
    const roleDef = laborRates.find(r => r.roleKey === roleKey) || equipmentRates.find(r => r.roleKey === roleKey)
    try {
      if (updateEntity) {
        await axios.post(`${url}/api/bid-estimation/${endpoint}`, {
          roleKey,
          role: roleDef?.role,
          name: roleDef?.name,
          hourlyRate,
          defaultCrewSize: rateType === 'labor' ? defaultCrewSize : undefined,
          client: null,
          project: null
        })
      }
      if (updateClient && project?.clientId) {
        await axios.post(`${url}/api/bid-estimation/${endpoint}`, {
          roleKey,
          role: roleDef?.role,
          name: roleDef?.name,
          hourlyRate,
          defaultCrewSize: rateType === 'labor' ? defaultCrewSize : undefined,
          client: project.clientId,
          project: null
        })
      }
      setToast('Global defaults updated')
    } catch (err) {
      setToast(`Failed to update global: ${err.response?.data?.error || err.message}`)
    } finally {
      setModalChange(null)
    }
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
  }

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton size="small" onClick={() => history.push(`/bid-estimation/${projectId}`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" style={{ marginLeft: 8 }}>
          Rates — {project?.name || ''}
        </Typography>
      </Box>

      <Paper variant="outlined" style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" color="textSecondary">
          Rates edited here apply only to this bid. The "source" chip shows where each rate came from:
          <strong style={{ color: '#7b1fa2' }}> Project</strong> (bid override) ·
          <strong style={{ color: '#1976d2' }}> Client</strong> ({clientName || 'client override'}) ·
          <strong style={{ color: '#2e7d32' }}> Entity</strong> (organization default) ·
          <strong style={{ color: '#9e9e9e' }}> Default</strong> (canonical fallback)
        </Typography>
      </Paper>

      <Paper>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Labor Rates" />
          <Tab label="Equipment Rates" />
          <Tab label="Rate Change History" />
        </Tabs>
        <Box p={2}>
          {tab === 0 && (
            <InBidRateTable
              rates={laborRates}
              rateType="labor"
              onSave={(row, vals) => handleSave(row, vals, 'labor')}
              onReset={(row) => handleReset(row, 'labor')}
            />
          )}
          {tab === 1 && (
            <InBidRateTable
              rates={equipmentRates}
              rateType="equipment"
              onSave={(row, vals) => handleSave(row, vals, 'equipment')}
              onReset={(row) => handleReset(row, 'equipment')}
            />
          )}
          {tab === 2 && (
            <RateChangeHistory events={events} onRevert={handleRevert} />
          )}
        </Box>
      </Paper>

      <SaveAsGlobalModal
        open={!!modalChange}
        change={modalChange}
        clientName={clientName}
        onSkip={() => setModalChange(null)}
        onApply={handleApplyGlobal}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(InBidRatesView)
